import { Component, OnInit, OnDestroy, ChangeDetectorRef, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { RulesService } from '../../api/services/rules.service';
import { AppToastService } from '../../shared/toast-service/toast-service';
import { AuthService } from '../../shared/auth-service/auth.service';
import { MatingRuleModel, SectionModel, CrossSectionModel, TransformationModel, ArticleModel, QueryParams, SystemModelPagedResponseModel, SystemModel } from '../../api/models';
import { ArticlesService } from 'src/app/api/services/articles.service';
import { SystemsService } from 'src/app/api/services/systems.service';
import { FormBuilder, Validators } from '@angular/forms';

@Component({
    selector: 'app-mating-rule-edit',
    templateUrl: './mating-rule-edit.component.html',
    styleUrls: ['./mating-rule-edit.component.scss'],
    standalone: false,
})
export class MatingRuleEditComponent implements OnInit, OnDestroy {
    private destroy$ = new Subject<void>();


    // Component state
    loading = false;
    loggedIn = false;
    rule: MatingRuleModel | null = null;
    positionX: number = 0;
    positionY: number = 0;
    positionZ: number = 0;
    scaleX: number = 1;
    scaleY: number = 1;
    rotation: number = 0;
    isMirrored: boolean = false;
    imageUrl: string = '';
    editingState: { [key: string]: boolean } = {};
    originalValues: { [key: string]: any } = {};
    editingField: string | null = null;
    showArticleModal = false;
    showCrossSectionModal = false;
    articles: ArticleModel[] = [];
    selectedCrossSection: CrossSectionModel | null = null;
    isAddingArticle = false;
    systems: SystemModel[] = [];
    errorMessage: string = '';
    selectedSystem: SystemModel | null = null;
    currentCrossSection: CrossSectionModel | null = null;
    isNewCrossSection = false; // true when adding new cross-section
    
    // Sorting
    sortColumn: string = '';
    sortDirection: 'asc' | 'desc' = 'asc';

    newArticle: ArticleModel = {
        id: '',
        articleName: '',
        material: '',
        type: ''
    };

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private rulesService: RulesService,
        private toastService: AppToastService,
        private authService: AuthService,
        private cdr: ChangeDetectorRef,
        private articlesService: ArticlesService,
        private systemsService: SystemsService,
        private fb: FormBuilder
    ) { }

    ngOnInit(): void {
        this.loggedIn = !!this.authService.getToken();

        // Get rule ID from route
        this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const ruleId = params['id'];
            if (ruleId) {
                this.loadRule(ruleId);
            } else {
                this.toastService.show('Rule ID is required', {
                    classname: 'bg-danger text-white',
                    delay: 3000
                });
                this.router.navigate(['/rules/mating']);
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    logout(): void {
        this.authService.logout();
    }

    loadRule(id: string): void {
        this.loading = true;
        this.rulesService.getMatingRuleById(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (rule: any) => {
                    console.log('Loaded rule in edit page', rule);
                    // Always transform to ensure nested objects are properly converted
                    // Check if transformation is needed (either top-level or nested PascalCase)
                    const needsTransformation = rule && (
                        (rule as any).Sections || // Top-level PascalCase
                        (rule.Sections && rule.Sections.some((s: any) =>
                            s.CrossSections || s.SectionId || s.Description || s.ImageUrl ||
                            (s.crossSections && s.crossSections.some((cs: any) =>
                                cs.ArticleIds || cs.Articles || cs.Material || cs.Type || cs.Description
                            ))
                        ))
                    );
                    console.log('Needs transformation:', needsTransformation);
                    // Always transform rule into camelCase
                    if (rule && typeof rule === 'object') {
                        rule = this.transformRuleToCamelCase(rule);
                    }

                    this.rule = rule;
                    console.log('transformed rule in edit page', this.rule);
                    this.loading = false;
                    // Force change detection to ensure view updates
                    this.cdr.detectChanges();
                },
                error: (error: any) => {
                    console.error('Error loading rule:', error);
                    this.toastService.show('Error loading rule', {
                        classname: 'bg-danger text-white',
                        delay: 3000
                    });
                    this.loading = false;
                    this.router.navigate(['/rules/mating']);
                }
            });
    }

    goBack(): void {
        this.router.navigate(['/rules/mating']);
    }

    hasCrossSections(section: SectionModel): boolean {
        return !!(section.crossSections && section.crossSections.length > 0);
    }

    hasArticleIds(crossSection: CrossSectionModel | null | undefined): boolean {
        if (!crossSection) return false;
        const hasIds = !!(crossSection.articleIds && Array.isArray(crossSection.articleIds) && crossSection.articleIds.length > 0);
        const hasArticles = !!(crossSection.articles && Array.isArray(crossSection.articles) && crossSection.articles.length > 0);
        return hasIds || hasArticles;
    }

    getArticleIdsDisplay(crossSection: CrossSectionModel | null | undefined): string {
        if (!crossSection) return 'None';

        // First try to use articleIds if available
        if (crossSection.articleIds && Array.isArray(crossSection.articleIds) && crossSection.articleIds.length > 0) {
            const validIds = crossSection.articleIds.filter(id => id != null && id !== '');
            if (validIds.length > 0) {
                return validIds.join(', ');
            }
        }

        // Fallback: extract IDs from articles array if articleIds is not populated
        if (crossSection.articles && Array.isArray(crossSection.articles) && crossSection.articles.length > 0) {
            const ids = crossSection.articles
                .map(article => article?.id || article?.articleName || null)
                .filter(id => id != null && id !== '');
            if (ids.length > 0) {
                return ids.join(', ');
            }
        }

        return 'None';
    }

    getArticleIdsArray(crossSection: CrossSectionModel | null | undefined): string[] {
        if (!crossSection) return [];

        // First try to use articleIds if available
        if (crossSection.articleIds && Array.isArray(crossSection.articleIds) && crossSection.articleIds.length > 0) {
            const validIds = crossSection.articleIds.filter((id): id is string => id != null && id !== '');
            if (validIds.length > 0) {
                return validIds;
            }
        }

        // Fallback: extract IDs from articles array if articleIds is not populated
        if (crossSection.articles && Array.isArray(crossSection.articles) && crossSection.articles.length > 0) {
            const ids = crossSection.articles
                .map(article => article?.id || article?.articleName || null)
                .filter((id): id is string => id != null && id !== '');
            if (ids.length > 0) {
                return ids;
            }
        }

        return [];
    }

    transformationData: string = '';
    // currentCrossSection: CrossSectionModel | null = null;
    showTransformationModal = false;

    showTransformation(transformation: any, crossSection: any): void {
        this.currentCrossSection = crossSection;
        
         // Create a deep copy of the transformation to avoid shared references
    const transformCopy = {
        positionX: crossSection.transformation?.positionX ?? 0,
        positionY: crossSection.transformation?.positionY ?? 0,
        positionZ: crossSection.transformation?.positionZ ?? 0,
        scaleX: crossSection.transformation?.scaleX ?? 1,
        scaleY: crossSection.transformation?.scaleY ?? 1,
        rotation: crossSection.transformation?.rotation ?? 0,
        isMirrored: crossSection.transformation?.isMirrored ?? false
    };
    
    // Assign the copied values to component-level variables
    this.positionX = transformCopy.positionX;
    this.positionY = transformCopy.positionY;
    this.positionZ = transformCopy.positionZ;
    this.scaleX = transformCopy.scaleX;
    this.scaleY = transformCopy.scaleY;
    this.rotation = transformCopy.rotation;
    this.isMirrored = transformCopy.isMirrored;
    
    this.showTransformationModal = true;
    }

    private formatTransformationData(transformation: any): string {
        // Create a complete transformation object with all properties
        // This ensures all values are shown, including 0, null, and false
        // Null values are replaced with type-appropriate defaults
        const formatted: any = {
            positionX: this.getTransformationValue(transformation, 'positionX', 'PositionX', 0),
            positionY: this.getTransformationValue(transformation, 'positionY', 'PositionY', 0),
            positionZ: this.getTransformationValue(transformation, 'positionZ', 'PositionZ', 0),
            rotation: this.getTransformationValue(transformation, 'rotation', 'Rotation', 0),
            scaleX: this.getTransformationValue(transformation, 'scaleX', 'ScaleX', 1),
            scaleY: this.getTransformationValue(transformation, 'scaleY', 'ScaleY', 1),
            isMirrored: this.getTransformationValue(transformation, 'isMirrored', 'IsMirrored', false)
        };
        this.positionX = this.getTransformationValue(transformation, 'positionX', 'PositionX', 0);
        this.positionY = this.getTransformationValue(transformation, 'positionY', 'PositionY', 0);
        this.positionZ = this.getTransformationValue(transformation, 'positionZ', 'PositionZ', 0);
        this.scaleX = this.getTransformationValue(transformation, 'scaleX', 'ScaleX', 1);
        this.scaleY = this.getTransformationValue(transformation, 'scaleY', 'ScaleY', 1);
        this.rotation = this.getTransformationValue(transformation, 'rotation', 'Rotation', 0);
        this.isMirrored = this.getTransformationValue(transformation, 'isMirrored', 'IsMirrored', false);

        // JSON.stringify will show all properties including 0, null, and false
        return JSON.stringify(formatted, null, 2);
    }

    private mapPayloadForApi(rule: MatingRuleModel): any {
        return {
            id: rule.id || '',
            ruleId: rule.ruleId || '',
            name: rule.name || '',
            description: rule.description || '',
            systemId: rule.systemId || '',
            crossSections: (rule.crossSections || []).map(section => ({
                id: section.id || '',
                description: section.description || '',
                material: section.material || '',
                type: section.type || '',
                imageUrl: section.imageUrl || '',
                // Map articles: preserve id if present, send articleName/material/type
                articles: (section.articles || []).map((a: any) => ({
                    id: a.Id || a.id || '',
                    articleName: a.ArticleName || a.articleName || '',
                    material: a.Material || a.material || '',
                    type: a.Type || a.type || '',
                    systemIds: a.SystemIds || a.systemIds || [],
                    systems: a.Systems || a.systems || []
                })),
                articleIds: section.articleIds || [],
                transformation: {
                    positionX: section.transformation?.positionX ?? 0,
                    positionY: section.transformation?.positionY ?? 0,
                    positionZ: section.transformation?.positionZ ?? 0,
                    scaleX: section.transformation?.scaleX ?? 1,
                    scaleY: section.transformation?.scaleY ?? 1,
                    rotation: section.transformation?.rotation ?? 0,
                    isMirrored: section.transformation?.isMirrored ?? false
                }
            })),
            createdAt: rule.createdAt,
            updatedAt: rule.updatedAt
        };
    }

    saveRule(): void {
        if (!this.rule || !this.rule.id) {
            this.toastService.show('No rule to save', { classname: 'bg-warning text-light', delay: 2000 });
            return;
        }

        this.selectedSystem = this.systems.find(s => s.id === this.rule?.systemId) || null;

        // IMPORTANT: Update the currentCrossSection with the modal values BEFORE sending to API
        if (this.currentCrossSection) {
            this.currentCrossSection.transformation = {
                positionX: this.positionX,
                positionY: this.positionY,
                positionZ: this.positionZ,
                scaleX: this.scaleX,
                scaleY: this.scaleY,
                rotation: this.rotation,
                isMirrored: this.isMirrored
            };
            this.currentCrossSection.imageUrl = this.imageUrl;
            //   this.currentCrossSection.articles = this.articles; // ensure articles are updated
            //   this.currentCrossSection.articleIds = this.getArticleIdsArray(this.currentCrossSection); // sync articleIds
            console.log('Updated currentCrossSection:', this.currentCrossSection);
        }

        this.loading = true;
        console.log('Saving rule in saveRule Method', this.rule);
        const apiPayload = this.mapPayloadForApi(this.rule);

        console.log('API Payload being sent:', JSON.stringify(apiPayload, null, 2));

        this.rulesService.updateMatingRule(this.rule.id, apiPayload).subscribe({
            next: (response: any) => {
                console.log('Rule update response:', response);
                if (this.rule?.systemId) {

                    // Ensure array exists
                    if (this.selectedSystem && this.selectedSystem.id) {
                        if (!this.selectedSystem.matingRules) {
                            this.selectedSystem.matingRules = [];
                        }
                        // Add the new rule to the system
                        this.selectedSystem.matingRules.push(response);
                        this.systemsService.updateSystem(this.selectedSystem.id, this.selectedSystem)
                            .pipe(takeUntil(this.destroy$))
                            .subscribe({
                                next: (response) => {

                                },
                                error: (error) => {

                                }
                            });
                    }


                } else {
                    console.warn('No system id provided; skipping system update.');
                }
                this.toastService.show('Rule updated successfully', {
                    classname: 'bg-success text-light',
                    delay: 2000
                });


                this.loading = false;

                // Transform response and update the rule with the response from backend
                if (response) {
                    this.rule = this.transformRuleToCamelCase(response);
                    console.log('Rule updated with response:', this.rule);

                    // Re-find and update currentCrossSection to match the updated rule
                    if (this.currentCrossSection && this.rule.crossSections) {
                        const updatedSection = this.rule.crossSections.find(
                            s => s.id === this.currentCrossSection?.id
                        );
                        if (updatedSection) {
                            this.currentCrossSection = updatedSection;
                            console.log('CurrentCrossSection updated:', this.currentCrossSection);
                            //this.articles = this.currentCrossSection.articles || [];
                        }
                    }
                }

                // Close transformation modal
                this.showTransformationModal = false;
                this.closeTransformationModal();
            },
            error: (error: any) => {
                console.error('Error updating rule:', error);
                this.toastService.show('Error updating rule: ' + error.message, {
                    classname: 'bg-danger text-light',
                    delay: 3000
                });
                this.loading = false;
            }
        });
    }

    saveTransformation() {
        if (this.currentCrossSection) {
            const updatedTransformation = {
                positionX: this.positionX,
                positionY: this.positionY,
                positionZ: this.positionZ,
                scaleX: this.scaleX,
                scaleY: this.scaleY,
                rotation: this.rotation,
                isMirrored: this.isMirrored
            };

            //   this.transformationUpdated.emit({
            //     crossSection: this.currentCrossSection,
            //     transformation: updatedTransformation
            //   });
        }

        this.showTransformationModal = false;
        this.resetTransformationData();

    }

    closeTransformationModal(): void {
        // Keep the updated values in currentCrossSection with a NEW object
    // if (this.currentCrossSection) {
    //     this.currentCrossSection.transformation = {
    //         positionX: this.positionX,
    //         positionY: this.positionY,
    //         positionZ: this.positionZ,
    //         scaleX: this.scaleX,
    //         scaleY: this.scaleY,
    //         rotation: this.rotation,
    //         isMirrored: this.isMirrored
    //     };
    // }

    this.showTransformationModal = false;
    this.resetTransformationData();
    }

    private resetTransformationData(): void {
        this.positionX = 0;
        this.positionY = 0;
        this.positionZ = 0;
        this.scaleX = 1;
        this.scaleY = 1;
        this.rotation = 0;
        this.isMirrored = false;
        // this.currentCrossSection = null;
    }

    private getTransformationValue(transformation: any, camelKey: string, pascalKey: string, defaultValue: any): any {
        // Check camelCase first, then PascalCase
        let value: any;
        if (transformation[camelKey] !== undefined) {
            value = transformation[camelKey];
        } else if (transformation[pascalKey] !== undefined) {
            value = transformation[pascalKey];
        } else {
            // Property not found, return default
            return defaultValue;
        }

        // If value is null, return the default based on type
        if (value === null) {
            return defaultValue;
        }

        // Return the actual value (including 0, false, etc.)
        return value;
    }

    // TrackBy functions for better change detection performance
    trackBySectionId(index: number, section: SectionModel): string {
        return section.id || section.sectionId || `section-${index}`;
    }

    trackByCrossSectionId(index: number, crossSection: CrossSectionModel): string {
        return crossSection.id || `crossSection-${index}`;
    }

    // Transform PascalCase response to camelCase if API returns PascalCase
    // private transformRuleToCamelCase(rule: any): MatingRuleModel {
    //     return {
    //         id: rule.Id || rule.id,
    //         crossSections: (rule.Sections || rule.sections || []).map((section: any) => ({
    //             id: section.Id || section.id,
    //             sectionId: section.SectionId || section.sectionId,
    //             name: section.Name || section.name,
    //             description: section.Description || section.description,
    //            // imageUrl: section.ImageUrl || section.imageUrl,
    //             crossSections: (section.CrossSections || section.crossSections || []).map((cs: any) => ({
    //                 // id: cs.Id || cs.id,
    //                 description: cs.Description || cs.description,
    //                 material: cs.Material || cs.material,
    //                 type: cs.Type || cs.type,
    //                 imageUrl: cs.ImageUrl || cs.imageUrl,
    //                 transformation: this.transformTransformation(cs.Transformation || cs.transformation),
    //                 articleIds: cs.ArticleIds || cs.articleIds,
    //                 articles: (cs.Articles || cs.articles || []).map((article: any) => this.transformArticle(article))
    //             }))
    //         })),
    //         createdAt: rule.CreatedAt || rule.createdAt,
    //         updatedAt: rule.UpdatedAt || rule.updatedAt
    //     };
    // }

    private transformRuleToCamelCase(rule: any): MatingRuleModel {
        console.log('Transforming rule to camelCase:', rule);
        return {
            id: rule.Id || rule.id,
            ruleId: rule.RuleId || rule.ruleId,
            name: rule.Name || rule.name,
            description: rule.Description || rule.description,
            systemId: rule.SystemId || rule.systemId,

            crossSections: (rule.CrossSections || rule.crossSections || []).map((cs: any) => ({
                id: cs.Id || cs.id,
                description: cs.Description || cs.description,
                material: cs.Material || cs.material,
                type: cs.Type || cs.type,
                imageUrl: cs.ImageUrl || cs.imageUrl,
                transformation: this.transformTransformation(cs.Transformation || cs.transformation),
                articleIds: cs.ArticleIds || cs.articleIds,
                articles: (cs.Articles || cs.articles || []).map((a: any) => this.transformArticle(a))
            })),

            createdAt: rule.CreatedAt || rule.createdAt,
            updatedAt: rule.UpdatedAt || rule.updatedAt
        };
    }


    // Transform transformation object from PascalCase to camelCase
    private transformTransformation(transformation: any): TransformationModel {
        console.log("Raw transformation from API:", JSON.stringify(transformation, null, 2));

        if (!transformation || typeof transformation !== "object") {
            return {
                positionX: 0,
                positionY: 0,
                positionZ: 0,
                rotation: 0,
                scaleX: 1,
                scaleY: 1,
                isMirrored: false
            };
        }

        return {
            positionX: transformation.PositionX ?? transformation.positionX ?? 0,
            positionY: transformation.PositionY ?? transformation.positionY ?? 0,
            positionZ: transformation.PositionZ ?? transformation.positionZ ?? 0,
            rotation: transformation.Rotation ?? transformation.rotation ?? 0,
            scaleX: transformation.ScaleX ?? transformation.scaleX ?? 1,
            scaleY: transformation.ScaleY ?? transformation.scaleY ?? 1,
            isMirrored:
                transformation.IsMirrored ??
                transformation.isMirrored ??
                false
        };
    }

    // Transform article object from PascalCase to camelCase
    private transformArticle(article: any): any {
        if (!article) return article;
        if (typeof article !== 'object') return article;

        // Check if already in camelCase
        if (article.articleName !== undefined || article.id !== undefined) {
            return article;
        }

        // Transform from PascalCase
        return {
            id: article.Id || article.id,
            articleName: article.ArticleName || article.articleName,
            material: article.Material || article.material,
            type: article.Type || article.type,
            systemIds: article.SystemIds || article.systemIds,
            systems: (article.Systems || article.systems || []).map((system: any) => this.transformSystem(system)),
            createdAt: article.CreatedAt || article.createdAt,
            updatedAt: article.UpdatedAt || article.updatedAt
        };
    }

    // Transform system object from PascalCase to camelCase (if needed for nested systems)
    private transformSystem(system: any): any {
        if (!system) return system;
        if (typeof system !== 'object') return system;

        // Check if already in camelCase
        if (system.name !== undefined || system.id !== undefined) {
            return system;
        }

        // Basic transformation - only transform if needed
        return {
            id: system.Id || system.id,
            name: system.Name || system.name,
            material: system.Material || system.material,
            type: system.Type || system.type
        };
    }

    startEditing(crossSection: any, field: string) {
        this.editingField = crossSection.id + '-' + field;
    }

    stopEditing(crossSection: any) {
        this.editingField = null;

        // Auto-save when user clicks outside
        this.saveRule();
    }

    showArticleInformation(crossSection: CrossSectionModel | null | undefined): void {
        console.log('Show article information for crossSection:', crossSection);
        this.currentCrossSection = crossSection || null;
        // Ensure articles array exists on the cross section and use the SAME reference
        if (this.currentCrossSection && !this.currentCrossSection.articles) {
            this.currentCrossSection.articles = [];
        }
        this.articles = [];
        // Point component-level articles to the same array (no copy)
        //this.articles = this.currentCrossSection?.articles || [];
        this.currentCrossSection?.articleIds?.forEach(element => {
             this.articlesService.getArticleByName(element).subscribe({
              next: (article) => {
                const transformedArticle = this.transformArticle(article);
               this.articles.push(transformedArticle);
              }
            });
        });
        this.selectedSystem = this.systems.find(s => s.id === this.rule?.systemId) || null;
        console.log('Selected System on Submit:', this.selectedSystem);


        this.showArticleModal = true;
    }
    closeArticleModal(): void {
        this.showArticleModal = false;
    }

    addArticle() {
        this.isAddingArticle = true;

        this.newArticle = {
            id: '',
            articleName: '',
            material: '',
            type: '',
            systemIds: [],
            systems: []
        };
    }

    updateArticle(index: number, field: string, event: any) {
        const value = (event && event.target) ? event.target.value?.trim() ?? '' : (event ?? '');

        if (!this.currentCrossSection) return;

        // Defensive check
        if (!this.currentCrossSection.articles || !this.currentCrossSection.articles[index]) return;

        // Update the article field directly on the model
        (this.currentCrossSection.articles[index] as any)[field] = value;

        // Optional: mark the rule dirty, or set a local boolean "isDirty = true"
    }


    removeArticle(index: number) {
        if (!this.currentCrossSection) return;

        if (!this.currentCrossSection.articleIds || !this.currentCrossSection.articleIds[index]) return;
        const id = this.articles[index].id;
        this.articles.splice(index, 1);  // remove from UI table
       // this.currentCrossSection.articles.splice(index, 1);
       this.currentCrossSection.articleIds = [];
        this.articles.forEach(article => {
            this.currentCrossSection?.articleIds?.push(article.articleName!);
          });
         this.articlesService.deleteArticle(id!).subscribe({
              next: () => {
                  console.log('Article deleted successfully');
              }  
          });
        this.saveRule();


    }

    startEditingArticle(index: number, field: string) {
        this.editingField = `art-${index}-${field}`;
    }

    stopEditingArticle(index: number, field: string) {
        this.editingField = null;
        this.saveUpdatedArticle(index);
    }

    saveUpdatedArticle(index: number) {
        const updatedArticle = { ...this.articles[index] };

        if (!updatedArticle.id) {
            console.warn('Article ID is undefined; skipping update');
            return;
        }

        this.articlesService.updateArticle(updatedArticle.id, updatedArticle)
            .subscribe({
                next: (response) => {
                    console.log('Article updated successfully:', response);
                    const normalizedArticle = this.normalizeArticleResponse(response);
                    this.articles[index] = normalizedArticle;  // replace with saved article
                    this.saveRule();
                }
            });
    }

    private normalizeArticleResponse(apiResponse: any) {
        if (!apiResponse) return apiResponse;

        return {
            id: apiResponse.Id,
            articleName: apiResponse.ArticleName,
            material: apiResponse.Material,
            type: apiResponse.Type,
            systemIds: apiResponse.SystemIds,
            systems: apiResponse.Systems,
            createdAt: apiResponse.CreatedAt,
            updatedAt: apiResponse.UpdatedAt
        };
    }

    saveNewArticle() {
        if (!this.newArticle.articleName) {
            alert("All fields required");
            return;
        }
        this.newArticle.systemIds?.push(this.rule?.systemId || '');
        this.articlesService.createArticle(this.newArticle)
            .subscribe(created => {
                const normalizedArticle = this.normalizeArticleResponse(created);
                this.articles.push(normalizedArticle);  // add to UI table
                if (this.currentCrossSection) {
                    this.currentCrossSection.articles = this.articles; // ensure crossSection articles updated
                    this.currentCrossSection.articleIds?.push(normalizedArticle.articleName);
                    this.saveRule();
                }
                this.isAddingArticle = false;
            });
    }

    cancelAddArticle() {
        this.isAddingArticle = false;
    }

    getSystems(): void {
        const params: QueryParams = {
            page: 1
        };
        this.systemsService.getSystems(params).subscribe({
            next: (response: SystemModelPagedResponseModel | any) => {
                // Handle both camelCase (new) and PascalCase (old) formats
                const data = response.data || response.Data || [];
                // Transform systems from PascalCase to camelCase if needed
                this.systems = Array.isArray(data)
                    ? data.map(system => this.transformSystemIfNeeded(system))
                    : [];
                console.log('Loaded systems:', this.systems);

            },
            error: (error) => {
                console.error('Error loading systems:', error);
                this.showError('Could not load systems.');
            }
        });
    }

    // Transform system object from PascalCase to camelCase if needed
    private transformSystemIfNeeded(system: any): SystemModel {
        if (!system) return system;
        if (typeof system !== 'object') return system;

        // Check if already in camelCase (has lowercase properties)
        if (system.name !== undefined || system.id !== undefined) {
            // Already in camelCase, but check nested objects and return a new object to avoid mutation
            const transformed: SystemModel = {
                ...system,
                regions: system.regions && Array.isArray(system.regions)
                    ? system.regions.map((region: any) => this.transformRegionIfNeeded(region))
                    : system.regions
            };
            return transformed;
        }

        // Transform from PascalCase to camelCase
        const transformed: SystemModel = {
            id: system.Id || system.id,
            name: system.Name || system.name || '',
            version: system.Version || system.version,
            description: system.Description || system.description,
            material: system.Material || system.material,
            type: system.Type || system.type,
            regions: (system.Regions || system.regions || []).map((region: any) => this.transformRegionIfNeeded(region)),
            articleIds: system.ArticleIds || system.articleIds,
            articles: system.Articles || system.articles,
            createdAt: system.CreatedAt || system.createdAt,
            updatedAt: system.UpdatedAt || system.updatedAt
        };

        return transformed;
    }

    // Transform region object from PascalCase to camelCase if needed
    private transformRegionIfNeeded(region: any): any {
        if (!region) return region;
        if (typeof region !== 'object') return region;

        // Check if already in camelCase
        if (region.country !== undefined || region.id !== undefined) {
            return region;
        }

        // Transform from PascalCase to camelCase
        return {
            id: region.Id || region.id,
            country: region.Country || region.country,
            city: region.City || region.city,
            state: region.State || region.state,
            district: region.District || region.district,
            place: region.Place || region.place
        };
    }

    private showError(message: string): void {
        this.errorMessage = message;
        this.toastService.show(this.errorMessage, { classname: 'bg-danger text-light', delay: 2000 });
    }


    showCrossSectionInformation(): void {
        this.positionX = 0;
        this.positionY = 0;
        this.positionZ = 0;
        this.scaleX = 1;
        this.scaleY = 1;
        this.rotation = 0;
        this.isMirrored = false;
        this.isNewCrossSection = true;
        this.currentCrossSection = {
            id: '',
            description: '',
            material: '',
            type: '',
            articles: [],
            articleIds: [],
            imageUrl: '',
            transformation: {
                positionX: 0,
                positionY: 0,
                positionZ: 0,
                scaleX: 1,
                scaleY: 1,
                rotation: 0,
                isMirrored: false
            } as TransformationModel
        } as CrossSectionModel;

        // load modal fields


        this.articles = this.currentCrossSection.articles || [];

        this.showCrossSectionModal = true;
    }

    closeCrossSectionModal(): void {
        // we simply close the modal — if editing we kept object reference so changes remain
        this.showCrossSectionModal = false;
        this.currentCrossSection = null;
        this.isAddingArticle = false;
        this.isNewCrossSection = false;
    }

    // Save cross section (supports Add & Edit). Save button will already be disabled when invalid.
    saveCrossSection(): void {
        if (!this.currentCrossSection) return;

        // copy modal fields into model.transformation
        this.currentCrossSection.transformation = {
            positionX: Number(this.positionX) || 0,
            positionY: Number(this.positionY) || 0,
            positionZ: Number(this.positionZ) || 0,
            scaleX: Number(this.scaleX) || 1,
            scaleY: Number(this.scaleY) || 1,
            rotation: Number(this.rotation) || 0,
            isMirrored: !!this.isMirrored
        } as TransformationModel;

        // ensure rule exists
        if (!this.rule) this.rule = {} as MatingRuleModel;
        if (!this.rule.crossSections) this.rule.crossSections = [];

        if (this.isNewCrossSection) {
            // add new cross-section reference to rule
            this.rule?.crossSections.push(this.currentCrossSection);
        } else {
            // edit mode — the changes already reflect on the object reference in rule
            // optionally you could find and replace the cross-section by id if needed
        }

        // sync articleIds
       // this.currentCrossSection.articleIds = this.getArticleIdsArray(this.currentCrossSection);

        // persist the whole rule
        this.saveRule();

        // close modal
        this.showCrossSectionModal = false;
        this.currentCrossSection = null;
        this.isNewCrossSection = false;
    }

    // ---------- Validation (controls Save button enabled state) ----------
    isCrossSectionValid(): boolean {
        if (!this.currentCrossSection) return false;

        const descOk = !!(this.currentCrossSection.description && this.currentCrossSection.description.toString().trim().length > 0);
        const matOk = !!(this.currentCrossSection.material && this.currentCrossSection.material.toString().trim().length > 0);
        const typeOk = !!(this.currentCrossSection.type && this.currentCrossSection.type.toString().trim().length > 0);



        return descOk && matOk && typeOk;
    }

    // Sorting methods
    onSort(column: string): void {
        if (!this.rule || !this.rule.crossSections) return;
        
        if (this.sortColumn === column) {
            this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
        } else {
            this.sortColumn = column;
            this.sortDirection = 'asc';
        }
        this.applySorting();
    }

    applySorting(): void {
        if (!this.rule || !this.rule.crossSections || !this.sortColumn) {
            return;
        }

        this.rule.crossSections.sort((a, b) => {
            let aValue: any;
            let bValue: any;

            switch (this.sortColumn) {
                case 'id':
                    aValue = (a.id || '').toLowerCase();
                    bValue = (b.id || '').toLowerCase();
                    break;
                case 'description':
                    aValue = (a.description || '').toLowerCase();
                    bValue = (b.description || '').toLowerCase();
                    break;
                case 'material':
                    aValue = (a.material || '').toLowerCase();
                    bValue = (b.material || '').toLowerCase();
                    break;
                case 'type':
                    aValue = (a.type || '').toLowerCase();
                    bValue = (b.type || '').toLowerCase();
                    break;
                default:
                    return 0;
            }

            if (aValue < bValue) {
                return this.sortDirection === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return this.sortDirection === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }

    getSortIcon(column: string): string {
        if (this.sortColumn !== column) {
            return 'bi-arrow-down-up';
        }
        return this.sortDirection === 'asc' ? 'bi-arrow-up' : 'bi-arrow-down';
    }

}

